import { useNavigate, useParams } from 'react-router-dom';
import GameLayout from './GameLayout';
import styled from 'styled-components';
import { useEffect, useState } from 'react';
import { ChoiceBattleTheme } from '~/components/game/choice-battle-theme';
import { GQL_THEMES, StoredTheme } from '~/gql/theme/theme';
import { authClient } from '~/helpers/apollo';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import BattleQuestion from '~/components/game/battle-question';
import { useAuth } from '~/helpers/store/auth';
import iconBattle from '@assets/images/background/mode-battle.svg';
import iconVictory from '@assets/images/icon/icon-trophee-gold.png';
import Button from '~/components/ui/button';
import {
  GQL_AWARD_BONUS_CROWN,
  GQL_GET_BATTLE_TRACKING,
} from '~/gql/battles/battles';
import { BattleUserItem } from '~/components/ui/battle-user-item';
import { classic } from '~/themes';
import { BattleTrackItem } from '~/components/ui/battle-tracking-item';
import {
  SDivRules,
  SSpanText,
  SSpanTitleSmall,
} from '~/components/styles/styles';
import { RulesItem } from '~/components/ui/rules-item';
import RulesBattleDialog from '~/components/game/rules-battle-dialog';
import { renderNameOrYou } from '~/utils/common/utils';
import { Crown } from '~/components/game/crown';
import { MedalChoice } from '~/components/game/medal-choice';
import { useLingui } from '@lingui/react';
import QuitBattleDialog from '~/components/game/quit-battle-dialog';
import { ButtonQuit } from '~/components/ui/button-quit';

const NewBattle = () => {
  const { payload } = useAuth();
  const { id } = useParams();
  const { i18n } = useLingui();
  const navigate = useNavigate();
  const [isRulesOpen, setIsRulesOpen] = useState<boolean>(false);

  const [step, setStep] = useState<number>(1);
  const [theme, setTheme] = useState<StoredTheme>();
  const [themeId, setThemeId] = useState<string>();
  const [themes, setThemes] = useState<StoredTheme[]>([]);
  const [isQuitDialog, setIsQuitDialog] = useState<boolean>(false);
  const [turnEnded, setTurnEnded] = useState<boolean>(false);

  const [player, setPlayer] = useState<'challenger' | 'challenged' | undefined>(
    undefined
  );

  const [currentAward, setCurrentAward] = useState<'crown' | 'badge'>('crown');

  const [gameChallengerScore, setGameChallengerScore] = useState<{
    crownCount: number;
    badges: string[];
    turn: number;
  }>({
    crownCount: 0,
    badges: [],
    turn: 0,
  });

  const [gameChallengedScore, setGameChallengedScore] = useState<{
    crownCount: number;
    badges: string[];
    turn: number;
  }>({
    crownCount: 0,
    badges: [],
    turn: 0,
  });

  const [isCompleted, setIsCompleted] = useState<boolean>(false);

  const { data: battleTracking } = useQuery(GQL_GET_BATTLE_TRACKING, {
    client: authClient,
    fetchPolicy: 'network-only',
    variables: {
      battlePlayerTrackingId: id!,
    },
    onCompleted: (data) => {
      if (!data.getBattlePlayerTrackingById) {
        navigate('/mode-battle');
      }
      setPlayer(
        data.getBattlePlayerTrackingById.currentPlayerId ===
          data.getBattlePlayerTrackingById.challenger.id
          ? 'challenger'
          : 'challenged'
      );

      setTurnEnded(
        data.getBattlePlayerTrackingById.currentPlayerId !== payload?.uuid
      );
      setGameChallengerScore(data.getBattlePlayerTrackingById.challenger.score);
      setGameChallengedScore(data.getBattlePlayerTrackingById.challenged.score);
    },
  });

  const [fetchThemes] = useLazyQuery(GQL_THEMES, {
    client: authClient,
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      if (data) {
        setThemes(data.getThemes);
      }
    },
  });

  const [awardBonusCrown] = useMutation(GQL_AWARD_BONUS_CROWN, {
    client: authClient,
    variables: {
      battlePlayerTrackingId:
        battleTracking?.getBattlePlayerTrackingById.battlePlayerTrackingId!,
    },
    onCompleted: (data) => {
      if (player === 'challenger') {
        setGameChallengerScore({
          ...gameChallengerScore,
          crownCount: data.awardBonusCrown.crownCount,
        });
      } else {
        setGameChallengedScore({
          ...gameChallengedScore,
          crownCount: data.awardBonusCrown.crownCount,
        });
      }

      setThemeId(undefined);
      setTheme(undefined);
      if (!isCompleted) {
        setCurrentAward('badge');
        setStep(4);
      }
    },
  });

  useEffect(() => {
    fetchThemes();
  }, []);

  useEffect(() => {
    if (player === 'challenger') {
      if (gameChallengerScore.badges.length === 5) {
        setIsCompleted(true);
      }
    }
  }, [gameChallengerScore.badges]);

  useEffect(() => {
    if (player === 'challenged') {
      if (gameChallengedScore.badges.length === 5) {
        setIsCompleted(true);
      }
    }
  }, [gameChallengedScore.badges]);

  const handleSelectTheme = (name: string) => {
    const selectedTheme = themes.find((el) => el.name === name);
    if (selectedTheme) {
      setThemeId(selectedTheme.id);
      setTheme(selectedTheme);
      if (!isCompleted) setStep(3);
    }
  };

  function handleFinishQuestion(
    delta: number,
    newGameScore:
      | {
          crownCount: number;
          badges: string[];
          turn: number;
        }
      | undefined
  ) {
    if (newGameScore) {
      if (player === 'challenger') {
        setGameChallengerScore(newGameScore);
      } else {
        setGameChallengedScore(newGameScore);
      }
    }
    if (delta > 0) {
      setStep(4);
    } else {
      setStep(2);
    }
  }

  async function handleBonus() {
    await awardBonusCrown();
  }

  useEffect(() => {
    if (player === 'challenger') {
      if (step === 2 && gameChallengerScore.crownCount === 3) {
        setTheme(undefined);
        setThemeId(undefined);
        setStep(5);
      }
    } else {
      if (step === 2 && gameChallengedScore.crownCount === 3) {
        setTheme(undefined);
        setThemeId(undefined);
        setStep(5);
      }
    }
  }, [step]);

  const renderStep = () => {
    switch (step) {
      case 1: {
        return (
          <SDivIntro onClick={() => setStep((prev) => prev + 1)}>
            <SDivIntroContent>
              <SDivIntroTitle>
                <img src={iconBattle} alt="" />
              </SDivIntroTitle>
              <SDivIntroPlayers>
                <BattleUserItem
                  name={renderNameOrYou(
                    battleTracking?.getBattlePlayerTrackingById?.challenger
                      .username,
                    payload?.username
                  )}
                  alt="challenger-avatar"
                  avatarSrc={
                    battleTracking?.getBattlePlayerTrackingById?.challenger
                      ?.avatar?.fileName
                  }
                />
                <SSpan>VS</SSpan>
                <BattleUserItem
                  name={renderNameOrYou(
                    battleTracking?.getBattlePlayerTrackingById?.challenged
                      .username,
                    payload?.username
                  )}
                  alt="challenger-avatar"
                  avatarSrc={
                    battleTracking?.getBattlePlayerTrackingById?.challenged
                      ?.avatar?.fileName
                  }
                />
              </SDivIntroPlayers>
            </SDivIntroContent>
            <SDivAdvice>
              <SSpanTitleSmall $color={classic.token?.colorWhite}>
                {i18n._('battle.crown.advice')}
              </SSpanTitleSmall>
            </SDivAdvice>
          </SDivIntro>
        );
      }
      case 2: {
        return (
          <>
            {step !== 2 && <>erreur 1</>}
            {step === 2 &&
            (player === 'challenger'
              ? gameChallengerScore.crownCount !== 3
              : gameChallengedScore.crownCount !== 3) &&
            themes &&
            (player === 'challenger'
              ? gameChallengerScore.turn !== undefined
              : gameChallengedScore.turn !== undefined) ? (
              <ChoiceBattleTheme
                turn={
                  player === 'challenger'
                    ? gameChallengerScore.turn
                    : gameChallengedScore.turn
                }
                themes={themes}
                onSelectTheme={handleSelectTheme}
                onBonus={() => {
                  if (!isCompleted) {
                    void handleBonus();
                  }
                }}
              />
            ) : (
              <>erreur 2</>
            )}
          </>
        );
      }
      case 3: {
        return (
          theme &&
          !isCompleted && (
            <BattleQuestion
              onFinish={handleFinishQuestion}
              crownCount={
                player === 'challenger'
                  ? gameChallengerScore.crownCount
                  : gameChallengedScore.crownCount
              }
              themeId={themeId}
              battlePlayerTrackingId={
                battleTracking?.getBattlePlayerTrackingById
                  ?.battlePlayerTrackingId!
              }
              isBonusCrown={false}
              isChallenger={
                battleTracking?.getBattlePlayerTrackingById?.challenger
                  .username === payload?.username
              }
              setTurnEnded={setTurnEnded}
              setThemeId={setThemeId}
              setTheme={setTheme}
              theme={theme}
            />
          )
        );
      }
      case 4: {
        return (
          <Crown
            type={currentAward}
            onClick={() => {
              if (
                player === 'challenger'
                  ? gameChallengerScore.crownCount === 3
                  : gameChallengedScore.crownCount === 3
              ) {
                setTheme(undefined);
                setThemeId(undefined);
                setStep(5);
              } else {
                setStep(2);
              }
              setCurrentAward('crown');
            }}
          />
        );
      }
      case 5: {
        return (
          <>
            {!isCompleted && (
              <MedalChoice
                selectedMedals={
                  player === 'challenger'
                    ? gameChallengerScore.badges
                    : gameChallengedScore.badges
                }
                themes={themes}
                onSelect={handleMedalSelect}
              />
            )}
          </>
        );
      }
    }
  };

  function handleMedalSelect(medalName: string) {
    handleSelectTheme(medalName);
  }

  //   const currentChallengerScore = useMemo(() => {
  //     if (
  //       !battleTracking?.getBattlePlayerTrackingById?.challengerHistory ||
  //       battleTracking?.getBattlePlayerTrackingById?.challengerHistory.length ===
  //         0
  //     ) {
  //       return;
  //     }
  //     return battleTracking?.getBattlePlayerTrackingById?.challengerHistory?.find(
  //       (el) => el.turn === gameScore.turn
  //     );
  //   }, [gameScore.turn]);

  return (
    <GameLayout withoutHeader withoutFooter>
      <SDivBlur $background />

      <Container>
        {(step === 2 ||
          step === 4 ||
          step === 5 ||
          (step === 3 && isCompleted)) && (
          <BattleTrackItem
            playerName={
              battleTracking?.getBattlePlayerTrackingById?.challenger
                .username || ''
            }
            playerType="challenger"
            themes={themes}
            playerBadges={gameChallengerScore.badges}
            crownCount={gameChallengerScore.crownCount || 0}
            turn={gameChallengerScore.turn}
            isPlaying={player === 'challenger'}
          />
        )}

        {(step === 2 ||
          step === 4 ||
          step === 5 ||
          (step === 3 && isCompleted)) && (
          <BattleTrackItem
            playerName={
              battleTracking?.getBattlePlayerTrackingById?.challenged
                .username || ''
            }
            playerType="challenged"
            themes={themes}
            playerBadges={gameChallengedScore.badges}
            crownCount={gameChallengedScore.crownCount}
            isPlaying={player === 'challenged'}
            turn={gameChallengedScore.turn}
          />
        )}

        {(isCompleted || turnEnded) &&
        (step === 2 || step === 3 || step === 4 || step === 5) ? (
          <SFinalWrapper>
            <SDivFinal>
              {isCompleted && (
                <SDivIcon>
                  <img src={iconVictory} alt="" />
                </SDivIcon>
              )}
              <SSpanText
                $margin="10px 10px 20px 10px"
                $color={classic.token?.colorText}
              >
                {isCompleted && i18n._('battle.final')}
                {turnEnded && i18n._('battle.turnEnded')}
              </SSpanText>

              <Button color={'secondary'} onClick={() => navigate('/home')}>
                {i18n._('common.back')}
              </Button>
            </SDivFinal>
          </SFinalWrapper>
        ) : (
          renderStep()
        )}
      </Container>
      <SDivRules>
        <RulesItem onClick={() => setIsRulesOpen(true)} />
      </SDivRules>
      <RulesBattleDialog
        onClose={() => setIsRulesOpen(false)}
        open={isRulesOpen}
      />
      <QuitBattleDialog
        playerType={'challenger'}
        onClose={() => setIsQuitDialog(false)}
        open={isQuitDialog}
      />
      <SDivMenu>
        <ButtonQuit
          onClick={() => {
            if (isCompleted || turnEnded) {
              navigate('/home');
            } else {
              setIsQuitDialog(true);
            }
          }}
        />
      </SDivMenu>
    </GameLayout>
  );
};

export default NewBattle;

const SDivIcon = styled.div`
  position: absolute;
  top: -20px;
  left: 50%;
  transform: translateX(-50%);
  height: 50px;
  width: 50px;
  img {
    top: 0;
    left: 0;
    object-fit: contain;
    height: 100%;
    width: 100%;
  }
`;

const SDivMenu = styled.div`
  align-items: center;
  display: flex;
  justify-content: flex-end;
  left: 0;
  top: 0;
  position: absolute;
  padding: 5px;
  width: 100%;
  z-index: 2;

  @media screen and (orientation: portrait) {
    padding: 5px;
  }
  @media screen and (max-height: 450px) {
    padding: 5px;
  }
`;

const SDivFinal = styled.div`
  display: flex;
  border-radius: 24px;
  flex-direction: column;
  border: 3px solid #fff;
  align-items: center;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  padding: 30px;
  max-width: 500px;
  margin: 30px;
  position: relative;
`;

const SFinalWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100%;
  z-index: 2;
  position: relative;
`;

const SDivIntro = styled.div`
  align-items: center;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  width: 100%;
  position: relative;
`;

const SDivAdvice = styled.div`
  position: absolute;
  bottom: 20px;
  width: 70%;
  @media screen and (max-width: 450px) {
    bottom: 40px;
  }

  @media screen and (max-height: 450px) {
    bottom: 0;
  }
`;

const SDivIntroContent = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  height: 50%;
  width: 60%;

  @media screen and (max-width: 450px) {
    height: 50%;
    width: 90%;
  }
  @media screen and (max-height: 450px) {
    height: 90%;
    width: 60%;
  }
`;

const SDivIntroTitle = styled.div`
  height: 30%;
  width: 100%;
  position: relative;
  img {
    position: absolute;
    top: 0;
    left: 0;
    object-fit: contain;
    height: 100%;
    width: 100%;
  }
`;

const SDivIntroPlayers = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  height: 70%;
  width: 100%;
`;

export const SSpan = styled.span<{ $color?: string; $textAlign?: string }>`
  color: ${({ $color }) => ($color ? $color : `${classic.token?.colorWhite}`)};
  font-family: 'Riffic Free', sans-serif;
  font-weight: 700;
  font-size: 4rem;
  text-align: ${({ $textAlign }) => ($textAlign ? $textAlign : 'left')};

  @media screen and (max-height: 800px) {
    font-size: 3rem;
  }

  @media screen and (max-width: 450px) {
    font-size: 3rem;
  }
`;

// const SDiv = styled.div`
//   width: 100%;
//   display: flex;
//   justify-content: space-around;
// `;

const SDivBlur = styled.div<{ $background?: boolean }>`
  background: ${({ $background }) =>
    $background
      ? `linear-gradient(180deg, rgba(0, 202, 191, 0.60) 0%, rgba(0, 173, 212, 0.60) 100%)`
      : 'none'};
  backdrop-filter: ${({ $background }) =>
    $background ? `blur(18px)` : 'none'};
  height: 100%;
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
`;
